/**
 * Fullscreen Image Zoom — Mindful Sips
 * Circular gallery with prev/next, swipe, keyboard, dots.
 */

class FullscreenZoom {
  constructor() {
    this.productInfo = document.querySelector('[data-zoom-fullscreen]');
    if (!this.productInfo) return;

    this.images      = [];   // [{ src, alt }, …]
    this.currentIdx  = 0;
    this.overlay     = null;
    this.touchStartX = 0;
    this.isAnimating = false;

    this.init();
  }

  /* ── Setup ─────────────────────────────────────────────────────────────── */

  init() {
    this.collectImages();
    if (this.images.length === 0) return;
    this.createOverlay();
    this.suppressDefaultZoom();
    this.attachTriggers();
    this.attachOverlayEvents();
  }

  collectImages() {
    this.productInfo.querySelectorAll('.product__media-item').forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      this.images.push({ src: this.bestSrc(img), alt: img.alt || '' });
    });
  }

  bestSrc(img) {
    /* Pull the largest image from srcset, else inflate the src URL. */
    if (img.hasAttribute('srcset')) {
      let best = '', bestW = 0;
      img.getAttribute('srcset').split(',').forEach(entry => {
        const parts = entry.trim().split(' ');
        if (parts.length >= 2) {
          const w = parseInt(parts[1]);
          if (w > bestW) { bestW = w; best = parts[0]; }
        }
      });
      if (best) return best;
    }
    const src = img.src;
    if (src.includes('?')) {
      const [base, qs] = src.split('?');
      const params = new URLSearchParams(qs);
      const v = params.get('v');
      return v ? `${base}?v=${v}&width=5760` : `${base}?width=5760`;
    }
    return src;
  }

  /* ── Overlay DOM ────────────────────────────────────────────────────────── */

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'fz-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'Product image viewer');
    this.overlay.innerHTML = `
      <div class="fz-controls">
        <button class="fz-btn fz-prev" aria-label="Previous image">
          <svg width="12" height="22" viewBox="0 0 12 22" fill="none" aria-hidden="true">
            <polyline points="11,1 1,11 11,21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button class="fz-btn fz-close" aria-label="Close image viewer">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="17" y2="17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <line x1="17" y1="1" x2="1" y2="17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <button class="fz-btn fz-next" aria-label="Next image">
          <svg width="12" height="22" viewBox="0 0 12 22" fill="none" aria-hidden="true">
            <polyline points="1,1 11,11 1,21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      <div class="fz-stage">
        <img class="fz-img" src="" alt="" draggable="false">
      </div>
      <div class="fz-dots" role="tablist" aria-label="Image navigation"></div>
    `;
    document.body.appendChild(this.overlay);

    this.fzImg   = this.overlay.querySelector('.fz-img');
    this.fzDots  = this.overlay.querySelector('.fz-dots');
    this.fzPrev  = this.overlay.querySelector('.fz-prev');
    this.fzNext  = this.overlay.querySelector('.fz-next');
    this.fzClose = this.overlay.querySelector('.fz-close');
  }

  buildDots() {
    this.fzDots.innerHTML = '';
    const single = this.images.length <= 1;
    this.fzDots.style.display = single ? 'none' : '';
    this.fzPrev.style.display = single ? 'none' : '';
    this.fzNext.style.display = single ? 'none' : '';

    this.images.forEach((_, i) => {
      const btn = document.createElement('button');
      btn.className = 'fz-dot' + (i === this.currentIdx ? ' fz-dot--active' : '');
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Image ${i + 1} of ${this.images.length}`);
      btn.setAttribute('aria-selected', i === this.currentIdx ? 'true' : 'false');
      btn.addEventListener('click', () => this.goTo(i));
      this.fzDots.appendChild(btn);
    });
  }

  syncDots() {
    this.fzDots.querySelectorAll('.fz-dot').forEach((dot, i) => {
      const active = i === this.currentIdx;
      dot.classList.toggle('fz-dot--active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  /* ── Navigation ─────────────────────────────────────────────────────────── */

  goTo(idx) {
    /* Circular wrap */
    this.currentIdx = ((idx % this.images.length) + this.images.length) % this.images.length;
    const { src, alt } = this.images[this.currentIdx];

    /* Fade transition */
    this.fzImg.style.opacity = '0';
    this.fzImg.src = src;
    this.fzImg.alt = alt;
    this.fzImg.onload = () => { this.fzImg.style.opacity = '1'; };
    /* Fallback in case image is cached and onload doesn't fire */
    if (this.fzImg.complete) this.fzImg.style.opacity = '1';

    this.syncDots();
  }

  /* ── Open / Close ───────────────────────────────────────────────────────── */

  open(index) {
    this.currentIdx = index;
    this.buildDots();

    const { src, alt } = this.images[this.currentIdx];
    this.fzImg.style.opacity = '0';
    this.fzImg.src = src;
    this.fzImg.alt = alt;
    this.fzImg.onload = () => { this.fzImg.style.opacity = '1'; };
    if (this.fzImg.complete) this.fzImg.style.opacity = '1';

    this.overlay.classList.add('fz-overlay--open');
    document.documentElement.style.overflow = 'hidden';
    this.fzClose.focus();
  }

  close() {
    this.overlay.classList.remove('fz-overlay--open');
    document.documentElement.style.overflow = '';

    /* Sync the mobile slider so it shows the image the user just closed from. */
    if (window.innerWidth < 750) {
      const list = this.productInfo.querySelector('.product__media-list.slider');
      if (list) {
        const sw = list.clientWidth;
        /* Our infinite-clone script prepends a clone, so real slide i is at position i+1.
           It marks the list with data-infinite-cloned="true" when clones are in place. */
        const offset = list.dataset.infiniteCloned === 'true' ? 1 : 0;
        list.style.scrollBehavior = 'auto';
        list.scrollLeft = sw * (this.currentIdx + offset);
        list.style.scrollBehavior = '';
      }
    }
  }

  /* ── Event wiring ───────────────────────────────────────────────────────── */

  suppressDefaultZoom() {
    this.productInfo.querySelectorAll('.product__media-item a').forEach(link => {
      link.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); }, true);
      link.style.pointerEvents = 'none';
    });
    this.productInfo.querySelectorAll('[data-modal], [data-media-modal]').forEach(el => {
      el.style.pointerEvents = 'none';
    });
  }

  attachTriggers() {
    this.productInfo.querySelectorAll('.product__media-item').forEach((item, index) => {
      const img = item.querySelector('img');
      if (!img) return;
      img.style.cursor  = 'zoom-in';
      img.style.pointerEvents = 'auto';
      img.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.open(index);
      }, true);
    });
  }

  attachOverlayEvents() {
    /* Buttons */
    this.fzClose.addEventListener('click', () => this.close());
    this.fzPrev.addEventListener('click',  () => this.goTo(this.currentIdx - 1));
    this.fzNext.addEventListener('click',  () => this.goTo(this.currentIdx + 1));

    /* ── Unified touch handling ─────────────────────────────────────────────
       All three touch events share one touchMoved flag.
       • Mostly-horizontal swipe (dx > dy, dx > 40) → navigate prev/next
       • Mostly-vertical swipe → browser scrolls the image naturally (mobile)
       • No movement (clean tap) → click handler closes the overlay
    ────────────────────────────────────────────────────────────────────────── */
    let touchMoved  = false;
    let touchStartX = 0;
    let touchStartY = 0;

    this.overlay.addEventListener('touchstart', e => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
      touchMoved  = false;
    }, { passive: true });

    this.overlay.addEventListener('touchmove', () => {
      touchMoved = true;
    }, { passive: true });

    this.overlay.addEventListener('touchend', e => {
      /* When the user has pinch-zoomed in, horizontal panning should move
         within the enlarged image — not navigate to the next/prev slide. */
      if (window.visualViewport && window.visualViewport.scale > 1.05) return;

      const dx = touchStartX - e.changedTouches[0].screenX;
      const dy = Math.abs(e.changedTouches[0].screenY - touchStartY);
      if (Math.abs(dx) > 40 && Math.abs(dx) > dy) {
        dx > 0 ? this.goTo(this.currentIdx + 1) : this.goTo(this.currentIdx - 1);
      }
    }, { passive: true });

    /*
      Desktop: tap anywhere outside a button or dot to close.
      Mobile: only the X button closes — tapping the image should NOT close
      (users may double-tap to zoom or pan around an enlarged image).
    */
    this.overlay.addEventListener('click', e => {
      if (touchMoved) return;
      if (window.innerWidth < 750) return;
      if (!e.target.closest('.fz-btn') && !e.target.closest('.fz-dot')) {
        this.close();
      }
    });

    /* Keyboard */
    document.addEventListener('keydown', e => {
      if (!this.overlay.classList.contains('fz-overlay--open')) return;
      if (e.key === 'Escape')     this.close();
      if (e.key === 'ArrowLeft')  this.goTo(this.currentIdx - 1);
      if (e.key === 'ArrowRight') this.goTo(this.currentIdx + 1);
    });
  }
}

/* ── Boot ─────────────────────────────────────────────────────────────────── */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FullscreenZoom());
} else {
  new FullscreenZoom();
}
